import * as Sentry from "@sentry/electron/renderer";

import { SceneSyncChannel, type BroadcastConnectionStats } from "./sceneSync";

import {
  getEncodersConfig,
  resolutionForProfile,
} from "../entities/webcodecs/config";

/** Code (contrato IPC con el main: osrBroadcastHandlers) para "no se pudo
 * inicializar NINGÚN encoder de video por hardware". En win32 el main prueba
 * NVENC (NVIDIA) y, si falla, cae a Media Foundation (AMD/Intel) → este code
 * solo llega si AMBOS fallan. La UI lo mapea a un mensaje claro. DEBE coincidir
 * con el string del main. */
export const NO_HW_ENCODER = "NO_HW_ENCODER";

/** Error de arranque del broadcast que lleva un `code` estable (p.ej.
 * NO_HW_ENCODER) para que la UI muestre un mensaje claro según la causa. */
export class BroadcastStartError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = "BroadcastStartError";
    this.code = code;
  }
}

/**
 * Lado control: el engine de broadcast (Radio/composición/NVENC) vive en el OSR window.
 * El control NO transmite por sí mismo; dispara el OSR window (que renderiza + NVENC encodea)
 * y le manda los comandos start/stop por BroadcastChannel. (Directiva: engine → OSR, sin doble path.)
 */

const SESSION_ID = "eriol-broadcast";

const RING_NAME = "eriol-nvenc-broadcast";

const RING_CAPACITY = 8 * 1024 * 1024; // ~1.5s @ 25 Mbps/60fps

// Fallback si no hay config de encoder guardada (4K@60, IDR cada 2s).

const DEFAULTS = {
  width: 3840,

  height: 2160,

  fps: 60,

  bitrateKbps: 25000,

  gop: 120,
};

/**
 * Deriva los parámetros del OSR/NVENC desde el preset de encoder elegido por el usuario.
 * La resolución vive en el codecProfile (level H.264); fps/bitrate/keyframe son directos.
 * Así el broadcast respeta el preset (4K / 1440p / 1080p / 720p) en vez de hardcodear 4K.
 */

async function resolveBroadcastParams(): Promise<{
  width: number;

  height: number;

  fps: number;

  bitrateKbps: number;

  gop: number;
}> {
  try {
    const cfg = await getEncodersConfig();

    const { width, height } = resolutionForProfile(cfg.codecProfile);

    const fps = cfg.framerate > 0 ? cfg.framerate : DEFAULTS.fps;

    const bitrateKbps =
      cfg.bitrate > 0 ? Math.round(cfg.bitrate / 1000) : DEFAULTS.bitrateKbps;

    // GOP = frames por intervalo de keyframe (IDR). keyframeIntervalMs default 2000ms.

    const keyframeMs =
      cfg.keyframeIntervalMs > 0 ? cfg.keyframeIntervalMs : 2000;

    const gop = Math.max(1, Math.round((fps * keyframeMs) / 1000));

    return { width, height, fps, bitrateKbps, gop };
  } catch {
    return { ...DEFAULTS };
  }
}

// Re-export: el helper vive en windowMode.ts (sin dependencias) para que las entidades de audio y
// render puedan usarlo sin arrastrar Sentry ni la config de encoders.
export { isBroadcastWindow } from "./windowMode";

type OsrApi = {
  startOsrBroadcast?: (o: {
    sessionId: string;

    width: number;

    height: number;

    fps: number;

    ringName: string;

    ringCapacity: number;

    bitrateKbps?: number;

    gop?: number;
  }) => Promise<{ success: boolean; error?: string; code?: string }>;

  stopOsrBroadcast?: (id: string) => Promise<unknown>;
};

let controlChannel: SceneSyncChannel | null = null;

function channel(): SceneSyncChannel {
  if (!controlChannel) controlChannel = new SceneSyncChannel();

  return controlChannel;
}

// Suscriptores de la salud del broadcast que manda el OSR (control side).

type StatsListener = (stats: BroadcastConnectionStats) => void;

const statsListeners = new Set<StatsListener>();

/**
 * Suscribe a la salud del broadcast que el OSR reporta cada 1s.
 * La consume `useBroadcastStats` (BroadcastHealth en el title bar).
 */

export function onConnectionStats(listener: StatsListener): () => void {
  statsListeners.add(listener);

  return () => statsListeners.delete(listener);
}

/**
 * Suscribe (una sola vez) el canal de stats del OSR. Se llama en cada arranque de broadcast:
 * sin el guard, tras N emisiones cada foto llegaba N veces a los suscriptores.
 */

let statsListenerBound = false;

function initStatsListener(): void {
  if (statsListenerBound) return;

  statsListenerBound = true;

  channel().on((msg) => {
    if (msg.type === "stats" && msg.action === "connectionUpdate") {
      const payload = msg.payload;

      statsListeners.forEach((listener) => {
        try {
          listener(payload);
        } catch (err) {
          console.error("Stats listener error:", err);
        }
      });
    }
  });
}

/** Tiempo máximo para que el OSR confirme el arranque (carga de la app + escena + fuentes + kero). */
export const OSR_START_TIMEOUT_MS = 90_000;

/** Code de BroadcastStartError: el OSR no confirmó el arranque dentro de OSR_START_TIMEOUT_MS. */
export const OSR_START_TIMEOUT = "OSR_START_TIMEOUT";

/** Code de BroadcastStartError: el OSR reportó `broadcast-error` durante el arranque. El listener del
 * canal en el title bar ya muestra ese toast → la UI no debe repetirlo. */
export const OSR_START_FAILED = "OSR_START_FAILED";

/**
 * Espera la confirmación `broadcast-started` del OSR. Se suscribe ANTES de abrir la ventana para
 * no perderla. Sin confirmación en `timeoutMs` cierra el OSR (ventana + encoder) y rechaza: el
 * usuario ve un error en vez de un "Iniciando…" eterno o un "en vivo" falso.
 */
function waitForOsrStarted(timeoutMs = OSR_START_TIMEOUT_MS): {
  promise: Promise<void>;
  cancel: () => void;
} {
  let off: (() => void) | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const cleanup = () => {
    off?.();
    off = null;
    if (timer) clearTimeout(timer);
    timer = null;
  };
  const promise = new Promise<void>((resolve, reject) => {
    off = channel().on((msg) => {
      if (msg.type === "broadcast-started") {
        const t = msg.timeline;
        console.info(
          `[broadcast] OSR al aire en ${t.totalMs} ms (carga ${t.loadMs}, escena ${t.sceneMs}, ` +
            `fuentes ${t.sourcesMs}, stage ${t.stageMs}, start ${t.startMs}; reintentos de escena ${t.sceneRetries}; ` +
            `marcas ${JSON.stringify(t.marks)})`,
        );
        cleanup();
        resolve();
      } else if (msg.type === "broadcast-error") {
        cleanup();
        reject(new BroadcastStartError(msg.raw, OSR_START_FAILED));
      }
    });
    timer = setTimeout(() => {
      cleanup();
      void stopOsrBroadcastFromControl();
      reject(
        new BroadcastStartError(
          `El motor de transmisión no confirmó el arranque en ${Math.round(timeoutMs / 1000)} s`,
          OSR_START_TIMEOUT,
        ),
      );
    }, timeoutMs);
  });
  return { promise, cancel: cleanup };
}

/** Dispara el broadcast en el OSR window. El OSR auto-arranca al recibir la escena y confirma con
 * `broadcast-started`; recién entonces resuelve (el botón queda en "Iniciando…" hasta ahí). */

export async function startOsrBroadcastFromControl(): Promise<void> {
  const api = (window as unknown as { electronAPI?: OsrApi }).electronAPI;

  if (!api?.startOsrBroadcast) {
    throw new Error(
      "startOsrBroadcast no disponible (el broadcast requiere Electron)",
    );
  }

  const params = await resolveBroadcastParams();

  console.log(
    `[broadcast] preset → ${params.width}x${params.height}@${params.fps} ` +
      `${params.bitrateKbps}kbps GOP${params.gop}`,
  );

  // Suscripto antes de abrir el OSR: la confirmación no se puede perder.
  const started = waitForOsrStarted();

  const res = await api.startOsrBroadcast({
    sessionId: SESSION_ID,

    ringName: RING_NAME,

    ringCapacity: RING_CAPACITY,

    ...params,
  });

  if (!res?.success) {
    started.cancel();

    // Reportar a Sentry con contexto del renderer (preset/resolución). El main
    // ya capturó la excepción nativa cruda (LoadLibrary NvEncodeAPI64.dll); acá
    // sumamos el lado renderer para no depender solo del OSR window.
    Sentry.captureException(
      new Error(`startOsrBroadcast falló: ${res?.error ?? "desconocido"}`),
      {
        tags: { area: "broadcast-start", code: res?.code ?? "unknown" },
        extra: {
          preset: `${params.width}x${params.height}@${params.fps}`,
          nativeError: res?.error,
        },
      },
    );

    throw new BroadcastStartError(
      res?.error ?? "startOsrBroadcast falló",
      res?.code,
    );
  }

  // Initialize stats listener (idempotent)

  initStatsListener();

  // Por si el OSR ya estaba vivo (reuso de sesión): mandarle que arranque igual.

  channel().post({ type: "control", action: "start" });

  await started.promise;
}

/** Detiene el broadcast del OSR window (stop graceful + destruir la ventana/encoder). */

export async function stopOsrBroadcastFromControl(): Promise<void> {
  channel().post({ type: "control", action: "stop" });

  const api = (window as unknown as { electronAPI?: OsrApi }).electronAPI;

  await api?.stopOsrBroadcast?.(SESSION_ID);
}

/**
 * Lado OSR: publica la salud del broadcast hacia el control window.
 * La llama BroadcastComposition cada 1s con `radio.getConnectionStats()`.
 */

export function sendConnectionStats(stats: BroadcastConnectionStats): void {
  channel().post({ type: "stats", action: "connectionUpdate", payload: stats });
}
